import { Package, Truck, Plus, Trash2, Edit2 } from 'lucide-react';
import type { WorkOrderDetailViewModel, WorkOrderMaterial, WorkOrderEquipment } from '../types/workOrderDetail.types';

interface DetailResourcesTabProps {
  data: WorkOrderDetailViewModel;
  onAssign?: (type: 'material' | 'equipment') => void;
  onRemove?: (id: string, type: 'material' | 'equipment') => void;
}

export function DetailResourcesTab({ data, onAssign, onRemove }: DetailResourcesTabProps) {
  return (
    <div className="space-y-6">
      {/* Materials Section */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-emerald-50 text-emerald-600">
              <Package className="h-5 w-5" />
            </div>
            <h3 className="text-sm font-normal text-slate-900">Vật tư thi công</h3>
          </div>
          <button
            type="button"
            onClick={() => onAssign?.('material')}
            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-700 px-3 py-1.5 text-xs font-normal text-white hover:bg-emerald-800 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            Gán vật tư
          </button>
        </div>

        <div className="overflow-hidden rounded-md border border-slate-100 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 sm:text-sm text-xs font-normal text-slate-500">
              <tr>
                <th className="px-4 py-3 font-normal">Tên vật tư</th>
                <th className="px-4 py-3 font-normal">Đơn vị</th>
                <th className="px-4 py-3 text-right font-normal">Kế hoạch</th>
                <th className="px-4 py-3 text-right font-normal">Đã cấp</th>
                <th className="px-4 py-3 text-right font-normal">Đã dùng</th>
                <th className="px-4 py-3 text-center font-normal">Trạng thái</th>
                <th className="px-4 py-3 font-normal"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.materials.length > 0 ? (
                data.materials.map((m) => (
                  <ResourceRow 
                    key={m.id} 
                    item={m} 
                    type="material" 
                    onRemove={() => onRemove?.(m.id, 'material')} 
                  />
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                    Chưa có vật tư nào được gán.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Equipment Section */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-blue-50 text-blue-600">
              <Truck className="h-5 w-5" />
            </div>
            <h3 className="text-sm font-normal text-slate-900">Thiết bị thi công</h3>
          </div>
          <button
            type="button"
            onClick={() => onAssign?.('equipment')}
            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-700 px-3 py-1.5 text-xs font-normal text-white hover:bg-emerald-800 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            Điều động máy
          </button>
        </div>

        <div className="overflow-hidden rounded-md border border-slate-100 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 sm:text-sm text-xs font-normal text-slate-500">
              <tr>
                <th className="px-4 py-3 font-normal">Tên thiết bị</th>
                <th className="px-4 py-3 text-right font-normal">Giờ kế hoạch</th>
                <th className="px-4 py-3 text-right font-normal">Giờ thực tế</th>
                <th className="px-4 py-3 text-center font-normal">Trạng thái</th>
                <th className="px-4 py-3 font-normal"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.equipment.length > 0 ? (
                data.equipment.map((e) => (
                  <ResourceRow 
                    key={e.id} 
                    item={e} 
                    type="equipment" 
                    onRemove={() => onRemove?.(e.id, 'equipment')} 
                  />
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-slate-400">
                    Chưa có thiết bị nào được điều động.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function ResourceRow({ 
  item, 
  type, 
  onRemove 
}: { 
  item: WorkOrderMaterial | WorkOrderEquipment; 
  type: 'material' | 'equipment';
  onRemove: () => void;
}) {
  const isMaterial = type === 'material';
  const m = item as WorkOrderMaterial;
  const e = item as WorkOrderEquipment;

  return (
    <tr className="hover:bg-slate-50/50 transition-colors">
      <td className="px-4 py-3 font-normal text-slate-700">
        {isMaterial ? m.material_name : e.equipment_name}
      </td>
      {isMaterial && <td className="px-4 py-3 text-slate-500">{m.unit}</td>}
      <td className="px-4 py-3 text-right font-normal text-slate-600">
        {isMaterial ? m.planned_qty : e.planned_hours}
      </td>
      <td className="px-4 py-3 text-right text-slate-500">
        {isMaterial ? m.issued_qty : e.actual_hours}
      </td>
      {isMaterial && <td className="px-4 py-3 text-right text-emerald-600 font-normal">{m.used_qty}</td>}
      <td className="px-4 py-3 text-center">
        <span className={`inline-flex rounded-md px-2 py-0.5 text-[10px] font-normal  ${getStatusClasses(item.status_code)}`}>
          {getStatusLabel(item.status_code)}
        </span>
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex justify-end gap-1">
          <button className="p-1.5 text-slate-400 hover:text-emerald-600 transition-colors">
            <Edit2 className="h-4 w-4" />
          </button>
          <button onClick={onRemove} className="p-1.5 text-slate-400 hover:text-rose-600 transition-colors">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function getStatusClasses(status: string) {
  switch (status) {
    case 'assigned': return 'bg-blue-50 text-blue-600';
    case 'issued': return 'bg-amber-50 text-amber-600';
    case 'used':
    case 'completed': return 'bg-emerald-50 text-emerald-600';
    case 'cancelled': return 'bg-slate-100 text-slate-500';
    default: return 'bg-slate-50 text-slate-600';
  }
}

function getStatusLabel(status: string) {
  switch (status) {
    case 'assigned': return 'Đã gán';
    case 'issued': return 'Đã xuất kho';
    case 'used': return 'Đã dùng';
    case 'completed': return 'Hoàn thành';
    case 'cancelled': return 'Đã hủy';
    default: return status;
  }
}
